export enum GampCategory {
    Category1_Infrastructure = 1,
    Category2_Obsolete = 2,
    Category3_NonConfigured = 3,
    Category4_Configured = 4,
    Category5_Custom = 5,
}

type Lang = "pl" | "en";

export interface GampDictEntryText {
    label: string;
    description: string;
    examples: string;
    validationLevel: string;
}

export interface GampDictEntry {
    key: GampCategory;
    text: Record<Lang, GampDictEntryText>;
}

export const GAMP_DICTIONARY: GampDictEntry[] = [
    {
        key: GampCategory.Category1_Infrastructure,
        text: {
            pl: {
                label: "Kategoria 1 — Oprogramowanie infrastrukturalne",
                description:
                    "Oprogramowanie infrastrukturalne, nieużytkowe; nie przetwarza danych GMP, ale wspiera środowisko.",
                examples:
                    "System operacyjny, bazy danych, serwery aplikacyjne, środowiska wirtualne",
                validationLevel: "Niska — kwalifikacja instalacji (IQ) zwykle wystarczająca",
            },
            en: {
                label: "Category 1 — Infrastructure Software",
                description:
                    "Infrastructure software, non-user facing; does not process GMP data but supports the environment.",
                examples:
                    "Operating systems, databases, application servers, virtualization platforms",
                validationLevel: "Low — Installation Qualification (IQ) typically sufficient",
            },
        },
    },
    {
        key: GampCategory.Category2_Obsolete,
        text: {
            pl: {
                label: "Kategoria 2 — Narzędzia systemowe (nieaktualna)",
                description:
                    "Historyczna kategoria, połączona z Kategorią 1 w GAMP 5 (2022). Dotyczyła oprogramowania wspierającego systemy, np. narzędzi systemowych.",
                examples: "Narzędzia backupu, zarządzania systemem, narzędzia diagnostyczne",
                validationLevel: "Obecnie klasyfikowane jako Kategoria 1 — walidacja jak dla infrastruktury",
            },
            en: {
                label: "Category 2 — System Tools (obsolete)",
                description:
                    "Historical category merged into Category 1 in GAMP 5 (2022). Covered system tools supporting IT environments.",
                examples: "Backup utilities, system management tools, diagnostic tools",
                validationLevel: "Now classified as Category 1 — validation as infrastructure",
            },
        },
    },
    {
        key: GampCategory.Category3_NonConfigured,
        text: {
            pl: {
                label: "Kategoria 3 — Produkty nieskonfigurowane (COTS)",
                description:
                    "Standardowe oprogramowanie użytkowe (COTS), bez konfiguracji.",
                examples: "MS Excel, LIMS bez konfiguracji, aplikacje raportowe",
                validationLevel:
                    "Średnia — walidacja skupiona na funkcjach używanych w GMP",
            },
            en: {
                label: "Category 3 — Non-Configured Products (COTS)",
                description:
                    "Standard user software (COTS), no configuration.",
                examples: "MS Excel, LIMS without configuration, reporting apps",
                validationLevel:
                    "Medium — validation focused on functions used in GMP",
            },
        },
    },
    {
        key: GampCategory.Category4_Configured,
        text: {
            pl: {
                label: "Kategoria 4 — Produkty konfigurowane",
                description:
                    "Systemy z konfigurowalnym jądrem; użytkownik definiuje przepływy, reguły, raporty.",
                examples: "LIMS, MES, ERP, QMS",
                validationLevel:
                    "Wysoka — walidacja konfiguracji i krytycznych funkcji",
            },
            en: {
                label: "Category 4 — Configured Products",
                description:
                    "Systems with configurable core; user defines workflows, rules, reports.",
                examples: "LIMS, MES, ERP, QMS",
                validationLevel:
                    "High — validation of configuration and critical functions",
            },
        },
    },
    {
        key: GampCategory.Category5_Custom,
        text: {
            pl: {
                label: "Kategoria 5 — Oprogramowanie dedykowane",
                description:
                    "Oprogramowanie tworzone na zamówienie (bespoke), niestandardowe.",
                examples:
                    "Dedykowane systemy kontroli procesów, aplikacje webowe GMP",
                validationLevel:
                    "Bardzo wysoka — pełny cykl walidacji: URS → FS/DS → IQ/OQ/PQ",
            },
            en: {
                label: "Category 5 — Custom (Bespoke) Software",
                description:
                    "Bespoke, custom-developed software.",
                examples:
                    "Dedicated process control systems, GMP web applications",
                validationLevel:
                    "Very high — full validation lifecycle: URS → FS/DS → IQ/OQ/PQ",
            },
        },
    },
];

export const getGampEntry = (cat: GampCategory, lang: Lang) =>
    GAMP_DICTIONARY.find(x => x.key === cat)?.text[lang];

export const getGampLabel = (cat: GampCategory, lang: Lang) =>
    getGampEntry(cat, lang)?.label ?? String(cat);

export const toGampCategory = (v: unknown): GampCategory | undefined => {
    if (v === null || v === undefined) return undefined;

    const n = Number(v as any);
    if (!Number.isNaN(n) && n >= 1 && n <= 5) return n as GampCategory;

    const s = String(v);
    const map: Record<string, GampCategory> = {
        Category1_Infrastructure: 1,
        Category2_Obsolete: 2,
        Category3_NonConfigured: 3,
        Category4_Configured: 4,
        Category5_Custom: 5,
    };
    return map[s];
};